type AvatarLoader = () => Promise<Blob>;

const avatarBlobs = new Map<number, Blob | null>();
const pendingAvatarLoads = new Map<number, Promise<Blob | null>>();
const avatarGenerations = new Map<number, number>();

// Avatar images require a bearer token, so normal <img> HTTP caching cannot be
// used. This cache deduplicates protected requests while letting each component
// create and revoke its own object URL safely.
export const getAvatarBlob = async (userId: number, load: AvatarLoader): Promise<Blob | null> => {
  if (avatarBlobs.has(userId)) return avatarBlobs.get(userId) ?? null;

  const pending = pendingAvatarLoads.get(userId);
  if (pending) return pending;

  const generation = avatarGenerations.get(userId) ?? 0;
  let request: Promise<Blob | null>;
  request = load()
    .then((blob) => {
      // If an upload/delete happened while this request was in flight, discard
      // the old response and load the newly stored avatar instead.
      if ((avatarGenerations.get(userId) ?? 0) !== generation) {
        return getAvatarBlob(userId, load);
      }
      avatarBlobs.set(userId, blob);
      return blob;
    })
    .catch(() => {
      if ((avatarGenerations.get(userId) ?? 0) !== generation) {
        return getAvatarBlob(userId, load);
      }
      // Cache an absent avatar too; otherwise every initials fallback would
      // repeatedly make the same 404 request.
      avatarBlobs.set(userId, null);
      return null;
    })
    .finally(() => {
      if (pendingAvatarLoads.get(userId) === request) pendingAvatarLoads.delete(userId);
    });
  pendingAvatarLoads.set(userId, request);
  return request;
};

export const invalidateAvatar = (userId: number): void => {
  avatarBlobs.delete(userId);
  avatarGenerations.set(userId, (avatarGenerations.get(userId) ?? 0) + 1);
  pendingAvatarLoads.delete(userId);
};
